import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject, interval, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { ManagerAuthResponse } from '@dto/gateway-public/models';
import { BaseObject } from '@shared/base/base-object';
import { LocalStorageConstants } from '@shared/constants/local-storage-constants';
import { isLoginPath } from '@shared/helpers/redirect.service';
import { LocalStore } from '@shared/store/local-store';
import { NotificationsService } from 'app/notifications/notifications.service';
import { SettingsState } from '../states/settings.state';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class VersionService extends BaseObject {
  private readonly VERSION_CHECK_INTERVAL_TIME_SEC = 20;
  private stopListen$ = new Subject<void>();
  private pendingServerVersion: string | null = null;
  private dismissedServerVersion: string | null = null;

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private settingsState: SettingsState,
    private store: LocalStore,
    private auth: AuthService,
    private notificationsService: NotificationsService,
  ) {
    super();
  }

  public start(): void {
    this.auth.loggedIn$.pipe(takeUntil(this.destroy$)).subscribe((loggedIn) => {
      this.stopListen$.next();

      if (loggedIn) {
        this.startVersionCheck();
      }
    });

    this.auth.loggedOut$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.stopListen$.next();
    });
  }

  private startVersionCheck(): void {
    this.zone.runOutsideAngular(() => {
      interval(this.VERSION_CHECK_INTERVAL_TIME_SEC * 1000)
        .pipe(
          // The login page is loaded fresh and holds no table state, so there is nothing to
          // update there; a token left in storage must not make it prompt (RLN2-774).
          filter(() => !isLoginPath()),
          switchMap(() => this.hasNewVersion()),
          takeUntil(this.stopListen$),
        )
        .subscribe((hasNewVersion) => {
          if (hasNewVersion) {
            this.openUpdateDialog();
            this.stopListen$.next();
          }
        });
    });
  }

  private hasNewVersion(): Observable<boolean> {
    return this.http
      .get<ManagerAuthResponse>(this.settingsState.apiPath + '/auth/check', {
        observe: 'response',
        headers: {
          [LocalStorageConstants.TokenHeader]: localStorage.getItem(LocalStorageConstants.Token),
        },
      })
      .pipe(
        map((res) => {
          const serverVersion = res.headers.get('x-app-version');

          if (!serverVersion) {
            return false;
          }

          const localVersion = this.settingsState.version;

          // No local version means the session's storage was cleared (a logout in another tab),
          // not that a new bundle was deployed: prompting here was the "fake update" of RLN2-774.
          // A version the user already closed the prompt for is not asked about again either.
          if (
            !localVersion ||
            serverVersion === localVersion ||
            serverVersion === this.dismissedServerVersion
          ) {
            return false;
          }

          this.pendingServerVersion = serverVersion;
          return true;
        }),
        catchError(() => of(false)),
      );
  }

  private openUpdateDialog(): void {
    this.zone.run(() => {
      this.notificationsService.openVersionUpdateDialog().subscribe((result) => {
        if (!result || !this.auth.loggedIn) {
          this.pendingServerVersion = null;

          if (this.auth.loggedIn) {
            this.startVersionCheck();
          }
          return;
        }

        switch (result.closeType) {
          case 'YES':
          case 'TIMEOUT':
            this.commitPendingVersion();
            this.reloadSite();
            break;

          case 'CLOSE':
            this.dismissedServerVersion = this.pendingServerVersion;
            this.pendingServerVersion = null;
            this.startVersionCheck();
            break;
        }
      });
    });
  }

  private commitPendingVersion(): void {
    if (this.pendingServerVersion) {
      this.settingsState.version = this.pendingServerVersion;
      this.pendingServerVersion = null;
    }
  }

  private reloadSite(resetLocalStore: boolean = true): void {
    if (resetLocalStore) {
      this.store.clearTablesStore();
    }

    location.reload();
  }

  public stopListen(): void {
    this.stopListen$.next();
  }
}
